/**
 * NoticeFieldSet — 공지사항 전용 옵션 (고정 + 중요도)
 *
 * PostCreateModal에서 사용하는 서브폼 컴포넌트.
 */
import React from 'react';
import { Trans, t } from '@lingui/macro';
import { cn } from '../../../lib/utils';
import { Pin, AlertCircle, Info, Minus } from 'lucide-react';

/** 중요도 옵션 */
const IMPORTANCE_OPTIONS = [
  { value: '', label: t`Normal`, icon: Minus, color: 'text-muted-foreground' },
  { value: 'info', label: t`Notice`, icon: Info, color: 'text-warning' },
  { value: 'critical', label: t`Critical`, icon: AlertCircle, color: 'text-destructive' },
] as const;

interface NoticeFieldSetProps {
  isPinned: boolean;
  onIsPinnedChange: (value: boolean) => void;
  importance: string;
  onImportanceChange: (value: string) => void;
}

const NoticeFieldSet: React.FC<NoticeFieldSetProps> = ({
  isPinned, onIsPinnedChange, importance, onImportanceChange,
}) => (
  <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
    <span className="text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider">
      <Trans>Notice options</Trans>
    </span>

    {/* 고정 토글 */}
    <button
      type="button"
      onClick={() => onIsPinnedChange(!isPinned)}
      className={cn(
        'flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm transition-colors cursor-pointer',
        isPinned
          ? 'bg-primary/10 text-primary'
          : 'text-muted-foreground hover:bg-muted',
      )}
    >
      <Pin className={cn('size-4', isPinned && 'fill-primary/30')} strokeWidth={isPinned ? 2.5 : 1.5} />
      <span className="font-medium"><Trans>Pin to top</Trans></span>
      {isPinned && (
        <span className="ml-auto text-meta-sm bg-primary/15 px-1.5 py-0.5 rounded font-semibold">ON</span>
      )}
    </button>

    {/* 중요도 선택 */}
    <div className="space-y-1.5">
      <span className="text-meta-sm text-muted-foreground"><Trans>Importance</Trans></span>
      <div className="flex gap-1.5">
        {IMPORTANCE_OPTIONS.map(opt => {
          const Icon = opt.icon;
          const isActive = importance === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onImportanceChange(opt.value)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer border',
                isActive
                  ? 'border-current bg-background shadow-sm'
                  : 'border-transparent hover:bg-muted text-muted-foreground',
                isActive && opt.color,
              )}
            >
              <Icon className="size-3.5" />
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  </div>
);

export default NoticeFieldSet;
